/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {Entity} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {PartialSuccessType} from '@ion/dls/src/types/types';
import {formatEntityErrorMessage, formatErrorMessage, partialStatusMessageFormatter} from '@ion/dls/src/utils/ComponentUtils';

/**
 *
 * Types that could be used on user prompt notifications to potentially change colors,
 * icons, etc.
 */
export enum UserPromptTypes {
  Error = 'error',
  Warning = 'warning',
  Success = 'success'
};

/**
 *
 * Base options for error user prompts
 */
export const errorOptions = {
  display     : true,
  message     : 'common:errors.generic',
  actionLabel : 'common:retry',
  autoHide    : false,
  type        : UserPromptTypes.Error
};

/**
 *
 * Base options for success user prompts
 */
export const successOptions = {
  display             : true,
  message             : 'notifications:generic.success',
  actionLabel         : 'common:ok',
  autoHide            : true,
  partialSuccessState : null,
  type                : UserPromptTypes.Success
};

/**
 * Results for the successful add asset user prompt that extend the default options.
 *
 * ```typescript
 *
 *    dispatch(toggleUserPrompt(successAddOptions(Entity.role)));
 *
 * ```
 */
export const successAddOptions = (entity: Entity, cloned = false) => ({
  ...successOptions,
  message         : cloned ? 'common:editors.shared.successCloned' : 'common:editors.shared.successCreated',
  translateOption : {entity: translate(`common:entityLabels.${entity}`)}
});

const errorOptionsMessage = ({entity, err, useEntityFormatter, type}): string => {
  if (!isNil(err)) {
    return useEntityFormatter ? formatEntityErrorMessage(entity, err) : formatErrorMessage(err);
  }
  return `common:editors.shared.error${type}`;
};

/**
 * Results for the error add asset user prompt that extend the default options.
 *
 * ```typescript
 *
 *    dispatch(toggleUserPrompt(errorAddOptions(Entity.role)));
 *
 * ```
 */
export const errorAddOptions = (entity: Entity, err?: ServiceError, useEntityFormatter = false) => ({
  ...errorOptions,
  message         : errorOptionsMessage({entity, err, useEntityFormatter, type: 'Created'}),
  translateOption : {entity: translate(`common:entityLabels.${entity}`)}
});

/**
 *
 * Results for the successful edit asset user prompt that extend the default options.
 */
export const successEditOptions = (
  entity: Entity,
  state: PartialSuccessType = null,
  message = 'common:editors.shared.successEdited'
) => ({
  ...successAddOptions(entity),
  partialSuccessState : state ? partialStatusMessageFormatter(state.results, state.items) : null,
  message,
  state
});

/**
 *
 * Results for the error add asset user prompt that extend the default options.
 */
export const errorEditOptions = (entity: Entity, err?: ServiceError, useEntityFormatter = false) => ({
  ...errorAddOptions(entity, err),
  message : errorOptionsMessage({entity, err, useEntityFormatter, type: 'Edited'})
});

export const permissionMessage = () => ({
  ...errorOptions,
  message : 'devicefiles:noPermissionerror'
});

export const noFileMessage = (fileType: string) => ({
  ...errorOptions,
  message : translate('devicefiles:noFileError', {fileType})
});

/**
 * Results for the error clone user prompt that extend the default options.
 *
 * ```typescript
 *
 *    dispatch(toggleUserPrompt(errorCloneOptions(Entity.entityname)));
 *
 * ```
 */
export const errorCloneOptions = (entity: Entity, err?: ServiceError, useEntityFormatter = false) => ({
  ...errorOptions,
  message         : errorOptionsMessage({entity, err, useEntityFormatter, type: 'Cloned'}),
  translateOption : {entity: translate(`common:entityLabels.${entity}`)}
});
